import React, { useState, memo, useEffect, useMemo, useContext } from "react";
import {
  Row,
  Container,
  Card,
  CardBody,
  Col,
  Table,
  Button,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import PaginationComponent from "../../components/common/Pagination";
// Pages
import Breadcrumbs from "../../components/common/Breadcrumb";
import {
    handleAddVendorApi,
    getAllVendorsDetailsApi,
    handleChangeVendorStatusApi,
    fetchVendorDetailsWithDistributorsApi
} from "../../api/vendorApi";
import { getAllActiveDistributorsApi } from '../../api/distributorApi'
import { AuthContext } from "../../contexts/AuthContext";
import { useAppSettings } from '../../contexts/AppSettingsContext';

function Vendors() {
  const { appSettings } = useAppSettings();
  const itemsPerPage = process.env.REACT_APP_PAGINATION_ITEMS_PER_PAGE;  
  const appName = appSettings?.title || process.env.REACT_APP_APP_NAME;
  const [vendorModal, setVendorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedDistributors, setSelectedDistributors] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [vendors, setVendors] = useState([]);
  
  // #View modal
  const [vendorDataWithRelatedDistributors, setVendorDataWithRelatedDistributors] = useState({});
  const [viewModal, setViewModal] = useState(false);
  const [popUpMode, setPopUpMode] = useState("")                  
  const { authToken } = useContext(AuthContext);

  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0);

  // Set token
  useEffect(() => {
    if (authToken) {

      fetchAllActiveDistributors();
    }
  }, [authToken]);
  useEffect(()=>{
    if (authToken) {
      fetchAllVendors();
    }
  },[authToken, currentPage])

  const formattedSingleManufacturerDistributorDetails = useMemo(() => {
    if (popUpMode === "EDIT") {
      return vendorDataWithRelatedDistributors?.distributors?.map((item) => ({
        label: item.name,
        value: item.id
      }));
    } else {
      return [];
    }
  }, [vendorDataWithRelatedDistributors, popUpMode])
  useEffect(() => {
    if (popUpMode === "EDIT") {
      setSelectedDistributors(formattedSingleManufacturerDistributorDetails);
    }
  }, [formattedSingleManufacturerDistributorDetails, popUpMode]); 

  
  // #Handle customer form
  const vendorForm = useFormik({
    enableReinitialize : true,
    initialValues: {
      name: vendorDataWithRelatedDistributors?.name || "",
      email: vendorDataWithRelatedDistributors?.email ||"",
      contact_person: vendorDataWithRelatedDistributors?.contact_person || "",
      contact_no: vendorDataWithRelatedDistributors?.contact_no ||  "",
      emergency_contact_no: vendorDataWithRelatedDistributors?.emergency_contact_no ||  "",
      website_url:  vendorDataWithRelatedDistributors?.website_url || "",
      distributors : [],
      description : vendorDataWithRelatedDistributors?.description ||  "", // (if any additional notes )
      address: vendorDataWithRelatedDistributors?.address ||  ""
    },
    validationSchema: Yup.object({
      contact_no: Yup.string().matches(/^\S+$/, "Spaces are not allowed"),
      emergency_contact_no: Yup.string().matches(/^\S+$/, "Spaces are not allowed"),
    }),
    onSubmit: (values) => {

      // Trim all string values
      const trimmedValues =  Object.keys(values).reduce((acc, key)=>{
        acc[key]  = typeof values[key] == 'string' ? values[key].trim() : values[key]  
        return acc
      },{})
      trimmedValues.distributors = selectedDistributors.map((item)=>(item.value));
      trimmedValues.isEdit  =  false

      if(popUpMode == 'EDIT'){
          const userId = vendorDataWithRelatedDistributors?.id
          trimmedValues.isEdit  = true
          trimmedValues.id   = userId
      }
      console.log("submitted values ->", trimmedValues);
      handleAddVendor(trimmedValues);
      setSuccessMessage("");
    },
  });

  // Function handle open & close modal
  const toggle = () => {
    setVendorModal(!vendorModal);
    if (!vendorModal) {
      // # Reset form to initial values when modal is closed
      vendorForm.resetForm();
      setErrorMessage("");
      setSuccessMessage("");
      setPopUpMode("");
      setVendorDataWithRelatedDistributors({})
      setSelectedDistributors([])

    }
  };

  // Function handle Manufacture add functionality
  async function handleAddVendor(values) {
    try {
      if (!authToken) {
        throw new Error("Token not found.");
      }

      let response = await handleAddVendorApi(values, authToken);
      if (response?.code == 200) {
            setErrorMessage("");
            setSuccessMessage(response.message);
            fetchAllVendors(authToken);
            setVendorModal(false);
      } else {
            setSuccessMessage("");
            setErrorMessage(response?.message || "");
      }
    } catch (error) {
      console.log(error.message);
      setSuccessMessage("");
      setErrorMessage("Something went wrong");
    }
  }

  // Function for fetch all vendors
  async function fetchAllVendors() {
    try {
      let response = await getAllVendorsDetailsApi(authToken,  currentPage, itemsPerPage);
      if (response?.code == 200) {
           const responseData  =  response?.data
            setVendors(responseData?.manufacturers);
            setTotalPages(Math.ceil((responseData.totalCount || 1) / itemsPerPage));
      }
    } catch (err) {
      console.log("err->", err.message);
    }
  }

  // Function for change vendor status 
  const handleChangeVendorStatus = async(vendorId) =>{
    try{

      let response = await handleChangeVendorStatusApi(vendorId, authToken);
      if (response?.code == 200) {
            const updatedVendors = vendors.map((item)=>
            vendorId == item.id ?  {...item,status : item.status == 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'} : item)
            setVendors(updatedVendors);
      } 
    }catch(error){
        console.log("error",error);
    }
  }

  // <---------View Pop up------------>
  // Function handle open & close view  modal
  const handleShowViewModal = () => setViewModal(!viewModal);

  // Function for fetch particular vendor details
  const handleViewVendorDetails = async(vendorId) =>{
    try{
      
      if (!authToken) {
        throw new Error("Token not found.");
      }
      let response = await fetchVendorDetailsWithDistributorsApi(authToken, vendorId);
      if (response?.code == 200) {
        setVendorDataWithRelatedDistributors(response?.data || []);
        setViewModal(true);
      }

    }catch(err){
      console.log("err",err.message);
    }
  }

  
  // <---------Pop up------------>
  // Function for fetch active attributes
  async function fetchAllActiveDistributors() {
    try {
      
      if (!authToken) {
        throw new Error("Token not found.");
      }
      let response = await getAllActiveDistributorsApi(authToken);
      if (response?.code == 200) {
        setDistributors(response?.data || []);
      }
    } catch (err) {
      console.log("err->", err.message);
    }
  }

  // React icon
  const animatedComponents = makeAnimated();

  // Formatted distributors
  const formattedDistributors = useMemo(() => {
    if(!Array.isArray(distributors)){
      return []
    }
    return distributors?.map((item) => ({ value: item.id, label: item.name }));
  }, [distributors]);

  // Function for handle selected distributors
  const handleDistributorSelectChange = (selected) => {
    setSelectedDistributors(selected);
  };

// Function for fetch single  manufacture details based on the  vendor id
const fetchSingleManufactureDetails= async(vendorId) =>{
  try{
    if (!authToken) {
      throw new Error("Token not found.");
    }
    
    let response = await fetchVendorDetailsWithDistributorsApi(authToken, vendorId);
    if (response?.code == 200) {
      toggle();
      setVendorDataWithRelatedDistributors(response?.data || []);
    }
    setPopUpMode("EDIT");
  }catch(err){
    console.log("err",err.message);
  }
}

  let popupPropsData = {
    animatedComponents,
    formattedDistributors,
    handleDistributorSelectChange,
    selectedDistributors,
    vendorModal,
    toggle,
    vendorForm,
    errorMessage,
    successMessage,
    popUpMode
  }
  
  let propsViewModal = {
    handleShowViewModal,
    vendorDataWithRelatedDistributors,
    viewModal,

  }

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs title={appName} breadcrumbItem="Manufacturer" />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader tag="h5" className="p-1 ">
                <Button color="success" onClick={toggle}>
                  Add Manufacturer
                </Button>
              </CardHeader>
              <OpenPopUp {...popupPropsData} />
              <CardBody>
                <Table striped>
                  <thead>
                    <tr>
                      <th>S.No.</th>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Contact Person</th>
                      <th>Contact Number</th>
                      {/* <th>Website URL</th> */}
                      <th>View</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors?.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td style={{  color: 'rgb(47 123 191)', fontWeight: 'bold', backgroundColor: '#f0f8ff', padding: '10px'}}>{item.manufacture_code}</td>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>{item.contact_person}</td>
                        <td>{item.contact_no}</td>
                        {/* <td className="text-primary">
                            <a href={item.website_url} target="_blank" rel="noopener noreferrer" className="text-primary">
                              {item.website_url}
                            </a>
                        </td> */}
                        <td>
                          <Button color="success" type="button" onClick={()=>{handleViewVendorDetails(item.id)}}>
                            View
                          </Button>
                       
                        </td>
                        <td>
                          <Button color={`${item.status == 'ACTIVE' ? 'success' : 'danger'}`} type="button" onClick={()=>{handleChangeVendorStatus(item.id)}}>
                            {item.status}
                          </Button>
                        </td>
                        <td>
                          <Button  color={`success`} type="button" onClick={()=>{fetchSingleManufactureDetails(item.id)}}>
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <ViewModal {...propsViewModal}/>
                </Table>
              </CardBody>
              <div  className=" d-flex justify-content-end">
                <PaginationComponent currentPage={currentPage} setCurrentPage={setCurrentPage} totalPages={totalPages}/>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Vendors;

const OpenPopUp = ({
  animatedComponents,
  formattedDistributors,
  handleDistributorSelectChange,
  selectedDistributors,
  vendorModal,
  toggle,
  vendorForm,
  errorMessage,
  successMessage,
  popUpMode
}) => {


  return (
    <div>
      <Modal size="xl" isOpen={vendorModal} toggle={toggle}>
        <ModalHeader toggle={toggle}>{ popUpMode !== 'EDIT' ? 'Add Manufacturer' : 'Edit Manufacturer'}</ModalHeader>
        <Form onSubmit={vendorForm.handleSubmit}>
        <ModalBody>
            <Row>
              <Col md={12}>
                {errorMessage && (
                  <span className=" invalidError d-flex justify-content-center">
                    {errorMessage}
                  </span>
                )}
                {successMessage && (
                  <span className=" successMsg d-flex justify-content-center">
                    {successMessage}
                  </span>
                )}
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="vendor_name">Name:</Label>
                  <Input
                    type="text"
                    value={vendorForm.values.name}
                    required
                    onChange={vendorForm.handleChange}
                    id="vendor_name"
                    name="name"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="vendor_email">Email:</Label>
                  <Input
                    type="email"
                    id="vendor_email"
                    value={vendorForm.values.email}
                    required
                    onChange={vendorForm.handleChange}
                    name="email"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="vendor_contact_person">Contact Person:</Label>
                  <Input
                    type="text"
                    value={vendorForm.values.contact_person}
                    required
                    onChange={vendorForm.handleChange}
                    id="vendor_contact_person"
                    name="contact_person"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
              <FormGroup>
                  <Label htmlFor="vendor_contact_number">Contact Number:</Label>
                  <Input
                    type="text"
                    value={vendorForm.values.contact_no}
                    required
                    onChange={vendorForm.handleChange}
                    id="vendor_contact_number"
                    name="contact_no"
                  />
                </FormGroup>
                {vendorForm.errors.contact_no &&
                  vendorForm.errors.contact_no ? (
                    <div className=" invalidError d-flex justify-content-center">
                      {vendorForm.errors.contact_no}
                    </div>
                  ) : null}
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="vendor_emergency_contact_no">
                    Emergency Contact Number:
                  </Label>
                  <Input
                    type="text"
                    id="vendor_emergency_contact_no"
                    value={vendorForm.values.emergency_contact_no}
                    onChange={vendorForm.handleChange}
                    name="emergency_contact_no"
                  />
                </FormGroup>
                {vendorForm.errors.emergency_contact_no &&
                  vendorForm.errors.emergency_contact_no ? (
                    <div className=" invalidError d-flex justify-content-center">
                      {vendorForm.errors.emergency_contact_no}
                    </div>
                  ) : null}
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="vendor_website_url ">Website URL:</Label>
                  <Input
                    type="text"
                    id="vendor_website_url"
                    value={vendorForm.values.website_url}
                    onChange={vendorForm.handleChange}
                    name="website_url"
                  />
                </FormGroup>
              </Col>
              <Col  md={6}>
                <FormGroup>
                    <Label htmlFor="vendor_description">Select Distributors:</Label>
                    <Select
                      className="w-75"
                      closeMenuOnSelect={false}
                      components={animatedComponents}
                      isMulti
                      options={formattedDistributors}
                      value={selectedDistributors}
                      onChange={handleDistributorSelectChange}
                    />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label htmlFor="vendor_description">Notes:</Label>
                  <textarea
                    className="selectInput"
                    id="vendor_description"
                    name="description"
                    value={vendorForm.values.description}
                    onChange={vendorForm.handleChange}
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label htmlFor="vendor_contact_address">Contact Address</Label>
                  <textarea
                    className="selectInput"
                    id="vendor_contact_address"
                    name="address"
                    value={vendorForm.values.address}
                    onChange={vendorForm.handleChange}
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button type="submit" color="primary">
              Submit
            </Button>
            {/* </FormGroup> */}
            <Button color="secondary" onClick={toggle}>
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

const ViewModal = ({  handleShowViewModal, vendorDataWithRelatedDistributors, viewModal }) => {
  
  return (
    <Modal size={'lg' } isOpen={viewModal} toggle={handleShowViewModal} >
    
      <ModalBody className="p-5">
        {Object.keys(vendorDataWithRelatedDistributors)?.length !==0 ?
        <>
          <Row>
              {vendorDataWithRelatedDistributors.emergency_contact_no && <Col md={6}>
                <FormGroup>
                  <Label htmlFor="vendor_emergency_contact_no">
                    Emergency Contact Number:
                  </Label>
                  <Input
                    type="text"
                    id="vendor_emergency_contact_no"
                    value={vendorDataWithRelatedDistributors.emergency_contact_no}
                    readOnly={true}
                    name="emergency_contact_no"
                  />
                </FormGroup>
              </Col>}
              {vendorDataWithRelatedDistributors.description && <Col md={12}>
                <FormGroup>
                  <Label htmlFor="vendor_description">Note</Label>
                  <textarea
                    className="selectInput"
                    id="vendor_description"
                    name="vendor_description"
                    value={vendorDataWithRelatedDistributors.description}
                    readOnly={true}
                  />
                </FormGroup>
              </Col>}
              {vendorDataWithRelatedDistributors.address && <Col md={12}>
                <FormGroup>
                  <Label htmlFor="vendor_address">Address</Label>
                  <textarea
                    className="selectInput"
                    id="vendor_address"
                    name="vendor_address"
                    value={vendorDataWithRelatedDistributors.address}
                    readOnly={true}
                  />
                </FormGroup>
              </Col>}
          </Row>
          <h4>Distributors</h4>
          <Table bordered >
            <thead>
              <tr>
                <th>S.No.</th>
                <th >Code</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              {vendorDataWithRelatedDistributors?.distributors?.map((item,index)=>(
                  <tr key={index}>
                    <td>{index+1}</td>
                    <td style={{  color: 'rgb(47 123 191)', fontWeight: 'bold', backgroundColor: '#f0f8ff', padding: '10px'}}>{item.distributor_code}</td>
                    <td>{item.name}</td>
                  </tr>
              ))}
            </tbody>
          </Table>
        </> :
        <div  className=" p-5 d-flex justify-content-center align-item-center">
            <h1><b>No item found</b></h1>
        </div>}
      </ModalBody>
      <ModalFooter>
        <Button color="danger" onClick={handleShowViewModal}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

